/**
 * DashboardDocs — zona de documentación general de la empresa.
 *
 * Va encima de los KPIs del Dashboard y cubre tres cosas:
 *   1. Listado de TODOS los documentos de la empresa (los generados desde
 *      cualquier ficha y los creados a mano).
 *   2. Cada documento muestra sus elementos vinculados como enlaces al módulo
 *      correspondiente (server, switch, AP, servicio…), vía utils/docElements.
 *   3. Creación rápida de documentación suelta ("Nueva nota de empresa"):
 *      título + descripción → documento borrador → editor.
 *
 * Los datos salen de GET /api/docs (la propia API ya devuelve doc.links).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { docElementPath, docElementIcon, docElementLabel } from '../utils/docElements';
import './DashboardDocs.css';

const STATUS = {
  draft:     { label: 'Borrador',  cls: 'is-draft' },
  published: { label: 'Publicado', cls: 'is-published' },
};

const TYPE_ICON = { richtext: '📝', file: '📎', url: '🔗' };

const VISIBLE_ROWS = 5;

// Chips de elementos visibles por documento antes de resumirlos en «+N más»
const VISIBLE_LINKS = 6;

/**
 * Las fechas que devuelve la API son UTC sin zona ("2026-10-07T09:50:02"), así
 * que new Date() las leería como hora local y un documento recién creado
 * aparecería con varias horas de antigüedad. Si no trae zona, se asume UTC.
 */
function parseDate(iso) {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = parseDate(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} d`;
  return parseDate(iso).toLocaleDateString('es-ES');
}

export default function DashboardDocs({ companyId, variant = 'compact' }) {
  const navigate = useNavigate();
  const isFull   = variant === 'full';
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [showAll,   setShowAll]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ title: '', description: '' });
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState('');
  const [openLinks, setOpenLinks] = useState({});   // doc.id -> mostrar todos los vínculos

  const load = useCallback(async () => {
    if (!companyId) { setDocs([]); return; }
    setLoading(true); setError('');
    try {
      const list = await api.getDocPages(companyId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || 'No se han podido cargar los documentos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return docs;
    return docs.filter((doc) => (
      (doc.title || '').toLowerCase().includes(term)
      || (doc.description || '').toLowerCase().includes(term)
      || (doc.links || []).some((l) => (l.element_name || '').toLowerCase().includes(term))
    ));
  }, [docs, search]);

  const totalLinks = useMemo(
    () => docs.reduce((n, doc) => n + ((doc.links || []).length), 0),
    [docs]
  );

  const visibleDocs = showAll || isFull ? filtered : filtered.slice(0, VISIBLE_ROWS);

  const openDoc = (doc) => {
    if (doc.doc_type === 'url' && doc.external_url) { window.open(doc.external_url, '_blank'); return; }
    navigate(`/documentation/${doc.id}`);
  };

  const openElement = (link) => {
    const to = docElementPath(link.element_type, link.element_id);
    if (to) navigate(to);
  };

  const createNote = async (event) => {
    event.preventDefault();
    const title = form.title.trim();
    if (!title) { setFormError('Ponle un título a la nota.'); return; }
    setSaving(true); setFormError('');
    try {
      const doc = await api.createDocPage(companyId, {
        title,
        description: form.description.trim() || null,
        doc_type: 'richtext',
        status: 'draft',
        tags: ['empresa'],
      });
      setForm({ title: '', description: '' });
      setShowForm(false);
      if (doc?.id) navigate(`/documentation/${doc.id}`);
      else load();
    } catch (e) {
      setFormError(e.message || 'No se ha podido crear la nota.');
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) return null;

  return (
    <section className={`dbd${isFull ? ' is-full' : ''}`}>
      <div className="dbd-head">
        <div className="dbd-head-copy">
          <h2 className="dbd-title">📚 Documentación de la empresa</h2>
          <p className="dbd-subtitle">
            {docs.length === 0
              ? 'Documentos y notas internas de la empresa, con enlace a los elementos que referencian.'
              : `${docs.length} documento${docs.length === 1 ? '' : 's'} · ${totalLinks} vínculo${totalLinks === 1 ? '' : 's'} a elementos de la infraestructura`}
          </p>
        </div>

        <div className="dbd-head-actions">
          {(isFull || docs.length > 3) && (
            <input
              className="dbd-search"
              type="text"
              placeholder="Buscar documento o elemento…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <button
            type="button"
            className="dbd-btn is-primary"
            onClick={() => { setShowForm((v) => !v); setFormError(''); }}
          >
            ➕ Nueva nota de empresa
          </button>
          <button type="button" className="dbd-btn" onClick={() => navigate('/documentation')}>
            Abrir biblioteca ↗
          </button>
        </div>
      </div>

      {showForm && (
        <form className="dbd-form" onSubmit={createNote}>
          <input
            autoFocus
            className="dbd-input"
            type="text"
            placeholder="Título de la nota (ej. «Contactos del CPD y accesos»)…"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <input
            className="dbd-input"
            type="text"
            placeholder="Descripción corta (opcional)…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="dbd-form-actions">
            <button type="submit" className="dbd-btn is-primary" disabled={saving}>
              {saving ? 'Creando…' : 'Crear y escribir'}
            </button>
            <button
              type="button"
              className="dbd-btn"
              onClick={() => { setShowForm(false); setForm({ title: '', description: '' }); setFormError(''); }}
            >
              Cancelar
            </button>
          </div>
          {formError && <div className="dbd-form-error">⚠️ {formError}</div>}
        </form>
      )}

      {loading && docs.length === 0 && <div className="dbd-empty">Cargando documentación…</div>}
      {error && <div className="dbd-error">⚠️ {error}</div>}

      {!loading && !error && docs.length === 0 && (
        <div className="dbd-empty">
          <span className="dbd-empty-icon">📄</span>
          <p className="dbd-empty-title">Todavía no hay documentación en esta empresa</p>
          <p className="dbd-empty-copy">
            Crea una nota con «➕ Nueva nota de empresa» o genera documentación desde la ficha de
            cualquier elemento (servidores, switches, servicios, WiFi…).
          </p>
        </div>
      )}

      {visibleDocs.length > 0 && (
        <ul className="dbd-list">
          {visibleDocs.map((doc) => {
            const st = STATUS[doc.status] || STATUS.draft;
            const links = [...(doc.links || [])].sort((a, b) => (
              docElementLabel(a.element_type).localeCompare(docElementLabel(b.element_type))
              || String(a.element_name || '').localeCompare(String(b.element_name || ''))
            ));
            const expanded = !!openLinks[doc.id];
            const shown = expanded ? links : links.slice(0, VISIBLE_LINKS);
            const hidden = links.length - shown.length;
            return (
              <li key={doc.id} className="dbd-row">
                <span className="dbd-row-icon">{TYPE_ICON[doc.doc_type] || '📝'}</span>

                <div className="dbd-row-main">
                  <div className="dbd-row-titleline">
                    <button type="button" className="dbd-row-title" onClick={() => openDoc(doc)}>
                      {doc.title}
                    </button>
                    <span className={`dbd-status ${st.cls}`}>{st.label}</span>
                    {doc.updated_at && <span className="dbd-row-date">{relativeTime(doc.updated_at)}</span>}
                    <button
                      type="button"
                      className="dbd-row-open"
                      title="Abrir el documento en el editor"
                      onClick={() => openDoc(doc)}
                    >
                      ↗
                    </button>
                  </div>

                  {doc.description && <p className="dbd-row-desc">{doc.description}</p>}

                  <div className="dbd-links">
                    {links.length === 0 && (
                      <span className="dbd-links-none">📄 Nota suelta · sin elementos vinculados</span>
                    )}
                    {shown.map((link) => {
                      const to = docElementPath(link.element_type, link.element_id);
                      const label = docElementLabel(link.element_type);
                      const text = link.element_name || `${label} #${link.element_id}`;
                      if (!to) {
                        return (
                          <span key={link.id} className="dbd-chip is-static" title={`${label} (sin módulo propio)`}>
                            {docElementIcon(link.element_type)} {text}
                          </span>
                        );
                      }
                      return (
                        <button
                          key={link.id}
                          type="button"
                          className="dbd-chip"
                          title={`${label} · ir al módulo`}
                          onClick={() => openElement(link)}
                        >
                          {docElementIcon(link.element_type)} {text}
                        </button>
                      );
                    })}
                    {hidden > 0 && (
                      <button
                        type="button"
                        className="dbd-chip is-more"
                        onClick={() => setOpenLinks((prev) => ({ ...prev, [doc.id]: true }))}
                      >
                        +{hidden} más
                      </button>
                    )}
                    {expanded && links.length > VISIBLE_LINKS && (
                      <button
                        type="button"
                        className="dbd-chip is-more"
                        onClick={() => setOpenLinks((prev) => ({ ...prev, [doc.id]: false }))}
                      >
                        Ver menos
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!isFull && filtered.length > VISIBLE_ROWS && (
        <button type="button" className="dbd-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Ver menos' : `Ver los ${filtered.length} documentos`}
        </button>
      )}

      {!loading && !error && docs.length > 0 && filtered.length === 0 && (
        <div className="dbd-empty">Ningún documento coincide con la búsqueda.</div>
      )}
    </section>
  );
}
